import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Icon, InputText, PageWrapper } from "@/deps/ui-library";
import { REPORTS, REPORT_GROUPS, readStarred } from "./catalog";
import { ReportRail } from "./ReportFrame";
import { useTaxWords } from "@/app/session";

/** The library of reports, with this viewer's starred ones first. */
export function AllReportsPage() {
  const say = useTaxWords();
  const [q, setQ] = useState("");
  const starred = readStarred();
  const needle = q.trim().toLowerCase();
  const found = REPORTS.filter(
    (r) =>
      needle === "" ||
      r.label.toLowerCase().includes(needle) ||
      r.description.toLowerCase().includes(needle),
  );
  const first = found.filter((r) => starred.includes(r.id));
  const rest = found.filter((r) => !starred.includes(r.id));

  const card = (report: (typeof REPORTS)[number]) => (
    <Link
      key={report.id}
      to={report.to as never}
      className="border-line bg-surface hover:border-primary flex flex-col gap-1 rounded-lg border p-3"
    >
      <span className="text-fg flex items-center gap-2 text-sm font-medium">
        <Icon name={report.icon as never} size="sm" />
        {say(report.label)}
      </span>
      <span className="text-fg-muted text-xs">{say(report.description)}</span>
    </Link>
  );

  return (
    <div className="flex items-start gap-6">
      <ReportRail />
      <PageWrapper spacing="md" className="min-w-0 flex-1 p-0">
        <PageWrapper.Header>
          <PageWrapper.Title>All reports</PageWrapper.Title>
        </PageWrapper.Header>
        <div className="flex items-center gap-3">
          <div className="w-72">
            <InputText
              aria-label="Search reports"
              placeholder="Search reports"
              value={q}
              onChange={(event) => setQ(event.target.value)}
            />
          </div>
          <span className="text-fg-muted text-xs">
            {found.length} {found.length === 1 ? "report" : "reports"}
          </span>
        </div>
        {found.length === 0 ? (
          <p className="text-fg-muted text-sm">No report matches "{q}".</p>
        ) : null}
        {first.length > 0 ? (
          <section className="flex flex-col gap-2">
            <h2 className="text-fg text-sm font-medium">Starred</h2>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{first.map(card)}</div>
          </section>
        ) : null}
        {REPORT_GROUPS.map((group) => {
          const inGroup = rest.filter((r) => r.group === group);
          return inGroup.length > 0 ? (
            <section key={group} className="flex flex-col gap-2">
              <h2 className="text-fg text-sm font-medium">{group}</h2>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{inGroup.map(card)}</div>
            </section>
          ) : null;
        })}
      </PageWrapper>
    </div>
  );
}
